import React, { useEffect, useMemo, useRef, useState } from "react";
import CommunityContext from "contexts/community";
import { IMailTemplate } from "reducers/community";
import { PageWithTabs } from "components/pageWithTabs";
import { Grid, List, ListItemText, TextField, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { LocalizeText } from "components/localizer";
import validator from "utils/validator";
import VKButton from "components/vkButton";
import { apiPatchGet, apiPost } from "fetchApi";
import { exhaustive } from "exhaustive";
import ConfirmDialog from "components/confirmDialog";
import { NotificationContext } from "contexts/notification";
import { ProfileContext } from "contexts/profile";
import WysisygEditor from "components/wysiwyg/wysiwygEditor";

const MailTemplateDetailsView: React.FC = () => {
    const { fetchMailTemplate } = React.useContext(CommunityContext);
    const [mailTemplate, setTemplate] = React.useState<IMailTemplate | null | "LOADING">(null);
    const { ...notification } = React.useContext(NotificationContext);
    const navigate = useNavigate();

    const params = useParams<{ mailTemplateId: string }>();
    useEffect(() => {
        if (params.mailTemplateId != null) {
            setTemplate("LOADING");
            fetchMailTemplate(params.mailTemplateId).then((template) => {
                if (template?.mail_template_type === "main_template") {
                    navigate("/staff-mail-templates/");
                }
                setTemplate(template);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.mailTemplateId]);

    const patchMailTemplate = async (mailTemplateId: string, subject: string, body: string): Promise<void> => {
        setTemplate("LOADING");
        interface PatchMailTemplate {
            subject: string;
            body: string;
        }
        const postBody: PatchMailTemplate = { subject: subject, body: body };

        const response = await apiPatchGet<IMailTemplate, PatchMailTemplate>(`/mail-triggers/${mailTemplateId}/`, postBody);
        exhaustive(response, "responseType", {
            Success: (it) => {
                notification.enqueNotification("success_updateMailTemplate");
                setTemplate(it.data);
            },
            Error: () => {
                notification.enqueNotification("error_updateMailTemplate");
                setTemplate(null);
            },
        });
    };

    const sendTestMail = async (testSubject: string, testBody: string): Promise<void> => {
        interface SendTestMail {
            mail_template_id: string;
            subject: string;
            body: string;
        }
        if (mailTemplate !== "LOADING" && mailTemplate != null) {
            const postBody: SendTestMail = { mail_template_id: mailTemplate.id, subject: testSubject, body: testBody };
            const response = await apiPost<SendTestMail>("/mail-triggers/test/", postBody);
            exhaustive(response, "responseType", {
                Success: () => {
                    notification.enqueNotification("success_sendTestMail");
                },
                Error: () => {
                    notification.enqueNotification("error_sendTestMail");
                },
            });
        } else {
            notification.enqueNotification("error_sendTestMail");
        }
    };

    if (mailTemplate === "LOADING" || mailTemplate === null) {
        return <div></div>;
    } else {
        return (
            <div className="contentWrapper">
                <PageWithTabs
                    titleTag="mailTemplate"
                    overTabsContent={
                        <Grid container flex="col" justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Typography variant="body2" marginBottom="20px">
                                    {"ID " + mailTemplate.id}, {mailTemplate.name}, {mailTemplate.community_name}
                                </Typography>
                            </Grid>
                        </Grid>
                    }
                    tabs={[
                        {
                            tag: "details",
                            handle: "details",
                            content: <MailTemplateDetails
                                originalTemplate={mailTemplate}
                                patchMailTemplate={patchMailTemplate}
                                sendTestMail={sendTestMail} />,
                        }
                    ]}
                />
            </div>
        );
    }
};

const MailTemplateDetails = (props: {
    originalTemplate: IMailTemplate,
    patchMailTemplate: (id: string, subject: string, body: string) => void,
    sendTestMail: (subject: string, body: string) => void
}): JSX.Element => {
    const { user } = React.useContext(ProfileContext);
    const [subjectState, setSubjectState] = useState<string>(props.originalTemplate.subject ? props.originalTemplate.subject : "");
    const bodyState = useRef<string>(props.originalTemplate.body ? props.originalTemplate.body : "");
    const [showModal, setShowModal] = useState<"SAVE" | "TEST" | null>(null);
    const [reloadEditor, setReloadEditor] = useState(0);

    const updateOpenMailTemplate = (newSubject: string | null, newBody: string | null): void => {
        if (newSubject != null) {
            setSubjectState(newSubject);
        }
        if (newBody != null) {
            bodyState.current = newBody;
        }
    };

    const resetFields = (): void => {
        bodyState.current = props.originalTemplate.body ?? "";
        setSubjectState(props.originalTemplate.subject ?? "");
        setReloadEditor(reloadEditor + 1);
    };

    const updateTemplate = (): void => {
        if ((props.originalTemplate.body === bodyState.current && props.originalTemplate.subject === subjectState) || fieldsAreEmpty) {
            setShowModal(null);
        } else {
            props.patchMailTemplate(props.originalTemplate.id, subjectState, bodyState.current);
        }
    };

    const sendTestMail = (): void => {
        props.sendTestMail(subjectState, bodyState.current);
        setShowModal(null);
    };


    const fieldsAreEmpty = bodyState.current === "" || subjectState === "";
    const confirmModalDescription = (
        <List>
            {fieldsAreEmpty ?
                <><ListItemText
                    primary={<LocalizeText tag="editMailTemplateFieldsAreEmpty" />}
                ></ListItemText></> :
                <>{props.originalTemplate.body === bodyState.current && props.originalTemplate.subject === subjectState ?
                    <ListItemText
                        primary={<LocalizeText tag="noChangesToBeSaved" />}
                    ></ListItemText> : <ListItemText
                        primary={<LocalizeText tag="confirmMailTemplateUpdateText" />}
                    ></ListItemText>}</>}


        </List>
    );

    const confirmSendTestModalBody = (
        <List>
            <ListItemText
                primary={<LocalizeText tag="confirmSendTestMailText" replace={{ email: user?.email ?? "" }} boldParams={true} />}
            ></ListItemText>
        </List>
    );

    return (
        <div>
            <div>
                <TextField
                    required
                    fullWidth
                    margin="normal"
                    label={<LocalizeText tag="mailSubject" />}
                    value={subjectState}
                    onChange={(event) => updateOpenMailTemplate(event.target.value, null)}
                    error={!validator.textFieldNotEmpty(subjectState)}
                />
                <WysisygEditor key={reloadEditor + "_editor"}
                    defaultValue={bodyState.current ? bodyState.current : ""}
                    onTextChange={(newText: string) => updateOpenMailTemplate(null, newText)} />
            </div>
            <div style={{ marginTop: "1rem", display: "flex", justifyContent: "space-between" }}>
                <div>
                    <VKButton
                        style={{ marginRight: "0.5rem", padding: "0.25rem 1.5rem 0.25rem 1.5rem" }}
                        size="small"
                        template="save"
                        onClick={() => setShowModal("SAVE")}>
                        <LocalizeText tag="save" />
                    </VKButton>
                    <VKButton
                        style={{ padding: "0.25rem 1.5rem 0.25rem 1.5rem" }}
                        tag="cancel"
                        size="small"
                        template="cancel"
                        onClick={() => resetFields()} />
                </div>
                <div>
                    <VKButton style={{ padding: "0.25rem 1.5rem 0.25rem 1.5rem" }} variant="outlined" size="small" onClick={() => setShowModal("TEST")}>
                        <LocalizeText tag="sendTestMail" />
                    </VKButton>

                </div>
            </div>
            <ConfirmDialog
                open={showModal === "SAVE"}
                title={<LocalizeText tag="confirmMailTemplateUpdateTitle" />}
                hideIcon={true}
                description={confirmModalDescription}
                hideRejectButton={(props.originalTemplate.body === bodyState.current && props.originalTemplate.subject === subjectState) || fieldsAreEmpty}
                onReject={() => setShowModal(null)}
                onAccept={() => updateTemplate()}
                acceptTag={(props.originalTemplate.body === bodyState.current && props.originalTemplate.subject === subjectState) || fieldsAreEmpty ? "close" : undefined}
            />
            <ConfirmDialog
                open={showModal === "TEST"}
                hideIcon={true}
                title={<LocalizeText tag="confirmSendTestMailTitle" />}
                description={confirmSendTestModalBody}
                onReject={() => setShowModal(null)}
                onAccept={() => sendTestMail()}
                acceptTag={"send"}
                rejectTag={"cancel"}
            />
        </div>
    );
};

export default MailTemplateDetailsView;
